import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  Collections_Enum,
  EmailTemplate,
} from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/email-template/v1/emailtemplate.pb';
import { CampaignType_Enum as CampaignType } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { EmailTemplateQueries } from '@frontend/api-email-template';
import {
  CustomTemplateCard,
  EmailTemplates,
  EmptyCustomTemplateState,
  useEmailTemplates,
} from '@frontend/email-templates';
import { useAppScopeStore } from '@frontend/scope';
import { useBulkMessageEditorContext } from '../../bulk-message-editor.context';
import { useBulkNavigator } from '../../hooks/use-bulk-navigator';
import { CONTAINER_HEIGHT } from './constants';

export const CustomListTemplates = ({ setIsUpdating }: { setIsUpdating: Dispatch<SetStateAction<boolean>> }) => {
  const { selectedOrgId: orgId, selectedLocationIds: appSelectedLocationIds } = useAppScopeStore();
  const { locationIds } = useBulkMessageEditorContext();
  const selectedLocationIds = locationIds.length ? locationIds : appSelectedLocationIds;
  const [isLoading, setIsLoading] = useState(false);

  const [pageToken, setPageToken] = useState<string | undefined>(undefined);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const listRequest = {
    orgId,
    locationIds: selectedLocationIds,
    pageToken,
    collection: Collections_Enum.BULK_MESSAGING,
  };

  const { data, fetchNextPage, hasNextPage, isFetched, isFetchingNextPage } =
    EmailTemplateQueries.useListEmailTemplates(listRequest);

  useEffect(() => {
    const newData = data?.pages?.[0]?.templates ?? [];
    const newTemplates = [...newData, ...templates];
    const filteredTemplates = newTemplates.filter(
      (template, index, arr) => arr.findIndex((t) => t.id === template.id) === index
    );
    setTemplates(filteredTemplates);
    return () => {
      setTemplates([]);
    };
  }, [pageToken, isFetched]);

  const getNextPage = () => {
    if (hasNextPage && !isFetchingNextPage) {
      setPageToken(data?.pages?.[0]?.nextPageToken);
      fetchNextPage();
    }
  };

  const navigateTo = useBulkNavigator(CampaignType.EMAIL);

  const { fetchOrgTemplate } = useEmailTemplates();

  const onTemplateClick = async (isEditingTemplate?: boolean, templateId?: string) => {
    if (!templateId) return;
    navigateTo.composer({ templateId, isEditingTemplate });
  };

  return (
    <EmailTemplates<EmailTemplate>
      DisplayedCard={(_index, template) => (
        <CustomTemplateCard
          key={template.id}
          {...template}
          collection={Collections_Enum.BULK_MESSAGING}
          onTemplateClick={(isEditingTemplate) => onTemplateClick(isEditingTemplate, template.id)}
          onTemplateFetch={() => fetchOrgTemplate(template.id)}
          selectedLocationIds={selectedLocationIds}
          setIsLoading={setIsLoading}
          setIsUpdating={setIsUpdating}
          setTemplates={setTemplates}
          templates={templates}
        />
      )}
      EmptyCardState={<EmptyCustomTemplateState onClick={navigateTo.composer} />}
      getNextPage={getNextPage}
      hasNextPage={hasNextPage}
      height={CONTAINER_HEIGHT}
      isFetched={isFetched}
      isFetchingNextPage={isFetchingNextPage}
      isLoading={isLoading}
      templates={templates}
    />
  );
};
